/**
 * Shared logic to build cart sections from quotation_requests.blocks + items.
 * All sections come from blocks.mix (full-container rows have quantity_containers on the row).
 * Legacy general.rows are migrated to mix in the worker on read.
 */

import type { QuotationDocBlocks, MixBlock, MixBlockRow } from '@/lib/store-types';
import { formatContainerType } from '@/lib/container-type';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';

/** Identifies where a row lives in blocks for admin patch (price per box, days). */
export type CartRowSource = { type: 'mix'; mixBlockIndex: number; rowIndex: number };

export interface CartLineRow {
  line: QuotationCartLine;
  quantity: number;
  unitLabel: string;
  rowLoadType?: 'pallets' | 'floor';
  /** Admin quotation: price per box and transport days (from blocks). */
  price_per_box_approx?: number;
  estimated_production_days?: number;
  estimated_arrival_days?: number;
  /** Where this row is in request.blocks for patching. */
  rowSource?: CartRowSource;
}

export interface CartSection {
  key: string;
  title: string;
  subtitle?: string;
  rows: CartLineRow[];
  mixBlock?: MixBlock;
  mixBlockIndex?: number;
}

export interface BuildSectionsOptions {
  /** When true, include general section even with 0 rows and mix blocks even with 0 rows (admin add-block UI). */
  includeEmptyBlocks?: boolean;
}

/** True when the row is full-container (one product = full space; only quantity of containers editable). */
export function isFullContainerRow(r: MixBlockRow): boolean {
  return r.quantity_containers != null;
}

/** True when the block is full-container only (single row with quantity_containers; cannot add another product). */
export function isFullContainerBlock(block: MixBlock): boolean {
  const rows = block.rows ?? [];
  return rows.length === 1 && rows[0] != null && isFullContainerRow(rows[0]);
}

/**
 * Returns indices of mix blocks that match the product's origin/port/supplier and can accept more products
 * (excludes full-container-only blocks). Used to show "add to which container" choices in the store.
 */
export function getMatchingMixBlockIndices(
  blocks: QuotationDocBlocks | undefined,
  item: { origin_id?: string | null; origin_port_id?: string | null; supplier_id?: string | null }
): number[] {
  const mix = blocks?.mix ?? [];
  const originId = item.origin_id ?? '';
  const portId = item.origin_port_id ?? '';
  const supplierId = item.supplier_id ?? '';
  const indices: number[] = [];
  mix.forEach((block, i) => {
    if (isFullContainerBlock(block)) return;
    if (block.origin_id !== originId || (block.origin_port_id ?? '') !== portId) return;
    if ((block.supplier_id ?? '') !== (supplierId ?? '')) return;
    indices.push(i);
  });
  return indices;
}

/** Build sections from request.blocks.mix only. Full-container rows use quantity_containers + container_type. */
export function buildSectionsFromBlocks(
  blocks: QuotationDocBlocks | undefined,
  items: QuotationCartLine[],
  options?: BuildSectionsOptions
): CartSection[] | null {
  const includeEmpty = options?.includeEmptyBlocks === true;
  const mixBlocks = blocks?.mix ?? [];
  const hasMix = mixBlocks.length > 0;
  if (!hasMix && !includeEmpty) return null;

  const itemBySourcingId = new Map(items.map((i) => [i.sourcingItemId, i]));
  const sections: CartSection[] = [];

  mixBlocks.forEach((block: MixBlock, idx: number) => {
    const blockRows = (block.rows ?? []) as MixBlockRow[];
    if (blockRows.length === 0 && !includeEmpty) return;
    const loadLabel = block.load_type === 'pallets' ? 'pallets' : 'cajas';
    const rows: CartLineRow[] =
      blockRows.length > 0
        ? blockRows.map((r: MixBlockRow, rowIndex: number) => {
            const isFull = isFullContainerRow(r);
            const quantity = isFull ? (r.quantity_containers ?? 0) : (r.boxes ?? 0);
            const line = itemBySourcingId.get(r.sourcing_item_id) ?? {
              sourcingItemId: r.sourcing_item_id,
              slug: '',
              name: `Producto ${r.sourcing_item_id.slice(0, 8)}`,
              moqValue: 1,
              moqUnitId: '',
              moqUnitLabel: isFull ? (r.container_type ? `Contenedor ${formatContainerType(r.container_type)}` : 'contenedores') : loadLabel,
              quantity,
              salesFormat: undefined,
              imageUrl: undefined,
              brandName: undefined,
              originName: undefined,
              unitsPerBox: undefined,
              boxesPerContainer: undefined,
            } as QuotationCartLine;
            return {
              line,
              quantity,
              unitLabel: isFull
                ? (r.container_type ? `Contenedor ${formatContainerType(r.container_type)}` : 'contenedores')
                : (line.moqUnitLabel || loadLabel),
              rowLoadType: r.row_load_type,
              price_per_box_approx: r.price_per_box_approx,
              estimated_production_days: r.estimated_production_days,
              estimated_arrival_days: r.estimated_arrival_days,
              rowSource: { type: 'mix', mixBlockIndex: idx, rowIndex },
            };
          })
        : [];
    const firstRow = blockRows[0];
    const isFullBlock = firstRow && isFullContainerRow(firstRow);
    // Full container: use line values as source of truth. Label includes "Contenedor" for clarity; total uses "contenedores" to avoid repeating the type.
    const containerLabel =
      isFullBlock && firstRow?.container_type
        ? `Contenedor ${formatContainerType(firstRow.container_type)}`
        : block.container_type
          ? `Contenedor ${formatContainerType(block.container_type)}`
          : 'Contenedor';
    const totalUnits = blockRows.reduce(
      (sum, r) => sum + (isFullContainerRow(r) ? (r.quantity_containers ?? 0) : (r.boxes ?? 0)),
      0
    );
    const unitLabelForTotal =
      isFullBlock
        ? 'contenedores'
        : rows.length > 0 && rows.every((r) => r.unitLabel === rows[0].unitLabel)
          ? rows[0].unitLabel
          : loadLabel;
    const title =
      block.name ??
      (isFullBlock && blockRows.length === 1
        ? `Bloque completo`
        : `Bloque mixto ${idx + 1}`);
    sections.push({
      key: `mix-${idx}`,
      title,
      subtitle: `${containerLabel} · ${blockRows.length} producto(s) · ${totalUnits.toLocaleString()} ${unitLabelForTotal}`,
      rows,
      mixBlock: block,
      mixBlockIndex: idx,
    });
  });

  return sections.length > 0 ? sections : null;
}

// === Price/weight totals per container block ===

export interface BlockPriceSummary {
  /** Peso total de 1 contenedor (kg). null si falta peso de alguna línea. */
  weightPerContainerKg: number | null;
  /** Precio de 1 contenedor (USD). null si falta precio de alguna línea. */
  pricePerContainerUsd: number | null;
  /** Precio parcial = pricePerContainerUsd × num_containers. null si pricePerContainerUsd es null. */
  partialPriceUsd: number | null;
}

/**
 * Calcula peso y precio por contenedor + precio parcial para un bloque mix.
 * Reglas de negocio:
 * - Si paletizada: row.boxes = pallets → cajas = pallets × boxes_per_pallet.
 * - Si a piso: row.boxes = cajas.
 * - Peso = Σ(cajas × gross_weight_per_box_kg).
 * - Precio por contenedor = Σ(cajas × price_per_box_approx).
 * - Precio parcial = precio por contenedor × num_containers.
 */
export function computeBlockPriceSummary(
  block: MixBlock,
  allItems: { id: string; boxes_per_pallet?: number; gross_weight_per_box_kg?: number; units_per_box?: number; boxes_per_container?: number; loading_method_id?: string }[],
  loadingMethods?: { id: string; name: string }[]
): BlockPriceSummary {
  let totalWeight = 0;
  let hasMissingWeight = false;
  let totalPrice = 0;
  let hasMissingPrice = false;

  for (const row of (block.rows ?? [])) {
    const r = row as MixBlockRow;
    const item = allItems.find((i) => i.id === r.sourcing_item_id);
    const bpp = item?.boxes_per_pallet ?? 0;
    const wPerBox = item?.gross_weight_per_box_kg ?? 0;

    // Tipo de carga efectivo: row > block > product default > floor
    let isPallet = false;
    if (r.row_load_type === 'pallets') isPallet = true;
    else if (r.row_load_type === 'floor') isPallet = false;
    else if (block.load_type === 'pallets') isPallet = true;
    else if (loadingMethods && item?.loading_method_id) {
      const methodName = loadingMethods.find((m) => m.id === item.loading_method_id)?.name;
      if (methodName && /palet/i.test(methodName)) isPallet = true;
    }

    let totalBoxes: number;
    if (r.quantity_containers != null) {
      totalBoxes = item?.boxes_per_container ?? 0;
    } else if (isPallet) {
      const pallets = r.boxes ?? 0;
      totalBoxes = pallets * (bpp > 0 ? bpp : 1);
      if (bpp <= 0) hasMissingWeight = true;
    } else {
      totalBoxes = r.boxes ?? 0;
    }

    if (wPerBox <= 0) hasMissingWeight = true;
    else totalWeight += totalBoxes * wPerBox;

    if (r.price_per_box_approx != null) totalPrice += totalBoxes * r.price_per_box_approx;
    else hasMissingPrice = true;
  }

  const numContainers = Math.max(1, block.num_containers ?? 1);
  return {
    weightPerContainerKg: hasMissingWeight ? null : totalWeight,
    pricePerContainerUsd: hasMissingPrice ? null : totalPrice,
    partialPriceUsd: hasMissingPrice ? null : totalPrice * numContainers,
  };
}

/**
 * Calcula el precio total global (suma de precios parciales de todos los bloques).
 * Retorna null si algún bloque tiene precio parcial indeterminado.
 */
export function computeGlobalTotalPrice(
  blocks: QuotationDocBlocks | undefined,
  allItems: { id: string; boxes_per_pallet?: number; gross_weight_per_box_kg?: number; units_per_box?: number; boxes_per_container?: number; loading_method_id?: string }[],
  loadingMethods?: { id: string; name: string }[]
): number | null {
  const mixBlocks = blocks?.mix ?? [];
  if (mixBlocks.length === 0) return null;
  let total = 0;
  for (const block of mixBlocks) {
    if ((block.rows ?? []).length === 0) continue;
    const summary = computeBlockPriceSummary(block, allItems, loadingMethods);
    if (summary.partialPriceUsd == null) return null;
    total += summary.partialPriceUsd;
  }
  return total;
}
